import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { 
  Heart, 
  Shield, 
  Users, 
  Building2, 
  Briefcase, 
  GraduationCap,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Euro,
  Baby,
  Clock
} from 'lucide-react';

const HealthcareSystemPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-100/30 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              Integration Module
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-6">
              Healthcare System in Germany
            </h1>
            <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
              Gesundheitssystem in Deutschland
            </p>
          </div>

          {/* Introduction Card */}
          <div className="bg-white rounded-2xl border border-babyblue-200 p-8 shadow-lg mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Heart className="w-7 h-7 text-red-500" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-3">
                  World-Class Healthcare at Your Service
                </h2>
                <p className="text-babyblue-600 leading-relaxed text-lg">
                  We all make contact with the healthcare system in Germany. Whether it is a quick check-up or a chronic disease, in Germany you will find care and safety in one of the best healthcare systems in the world.
                </p>
              </div>
            </div>
            
            <div className="bg-babyblue-50 rounded-xl p-6 border border-babyblue-200">
              <p className="text-babyblue-700 leading-relaxed">
                <strong className="text-babyblue-800">Here is a simple guide how to manage healthcare in Germany:</strong><br /><br />
                There are multiple options how to handle your healthcare. It is very important for you to make the necessary steps so you can make use of the medical services whenever you need them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Types of Insurance */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-4">
              Two Types of Health Insurance
            </h2>
            <p className="text-lg text-babyblue-600">
              Understanding your options for healthcare coverage in Germany
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Statutory Insurance */}
            <div className="bg-gradient-to-br from-babyblue-50 to-white rounded-2xl border-2 border-babyblue-200 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-babyblue-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-babyblue-500 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-babyblue-800">Statutory Insurance</h3>
                    <p className="text-primary text-sm font-medium">Gesetzliche Krankenversicherung (GKV)</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 border border-babyblue-200">
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-babyblue-800 mb-1">For Employees</p>
                        <p className="text-babyblue-600 text-sm">If you are an employee in Germany, your employer pays <strong className="text-babyblue-800">50% of your health insurance</strong>. You are responsible for the payment of the other half.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-babyblue-200">
                    <div className="flex items-start gap-3">
                      <Euro className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-babyblue-800 mb-1">Income Threshold</p>
                        <p className="text-babyblue-600 text-sm">The statutory insurance is intended for employees who make up to a gross income of <strong className="text-babyblue-800">€73,800</strong>.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-babyblue-200">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-babyblue-800 mb-1">Business Owners</p>
                        <p className="text-babyblue-600 text-sm">As a business owner, you can have a statutory insurance as well, but must cover it on your own expense. <strong className="text-babyblue-800">If you earn more, you pay more.</strong></p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-babyblue-200">
                    <div className="flex items-start gap-3">
                      <Baby className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-babyblue-800 mb-1">Family Coverage</p>
                        <p className="text-babyblue-600 text-sm">Children are covered by the parents' insurance up until age <strong className="text-babyblue-800">18</strong>, or until age <strong className="text-babyblue-800">25</strong> if they are still in university or vocational training.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-800 mb-1">Equal Treatment</p>
                        <p className="text-green-700 text-sm">All insured people receive the same treatment. The system works on a long-term basis where you can decide how and where you want to get your treatment.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-amber-800 mb-1">Important Note</p>
                        <p className="text-amber-700 text-sm">The long-term system cannot maintain all costs, which means depending on age and medical situations, some costs must be covered by yourself.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Private Insurance */}
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border-2 border-blue-200 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-babyblue-800">Private Insurance</h3>
                    <p className="text-blue-600 text-sm font-medium">Private Krankenversicherung (PKV)</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-babyblue-800 mb-1">Individual Calculation</p>
                        <p className="text-babyblue-600 text-sm">The insurance calculates based on your <strong className="text-babyblue-800">health status, age at start, and personal risk or illness history</strong>.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <Euro className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-babyblue-800 mb-1">Payment Process</p>
                        <p className="text-babyblue-600 text-sm">If you have private insurance, you <strong className="text-babyblue-800">pay your own medical bills first</strong>, then submit a request to the insurer who reimburses you.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-blue-800 mb-1">Who Can Switch?</p>
                        <p className="text-blue-700 text-sm">Not everyone can switch from statutory to private insurance. Eligible groups include:</p>
                        <ul className="text-blue-700 text-sm mt-2 space-y-1">
                          <li>• Civil servants</li>
                          <li>• Self-employed individuals</li>
                          <li>• Employees with income above <strong>€73,800</strong></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommendation */}
      <section className="py-16 px-6 bg-gradient-to-r from-primary to-babyblue-400">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
            Need More Information?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            For more information, we recommend you to contact a governmental or private insurance company and have a chat about the insurance options they offer.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://www.krankenkassen.de" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-primary hover:bg-babyblue-50 px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all"
            >
              Find Insurance Companies
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Related: Healthcare Career */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl border border-red-200 p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Heart className="w-8 h-8 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">
                  Want to Work in Healthcare?
                </h3>
                <p className="text-babyblue-600 mb-4">
                  Learn about the requirements and process to work as a nurse, caregiver, or doctor in Germany.
                </p>
                <Link 
                  to="/careers/healthcare"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Healthcare & Nursing Career Path
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Integration Modules */}
      <section className="py-16 px-6 bg-babyblue-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-8 text-center">
            Other Integration Modules
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <Building2 className="w-5 h-5" />, title: 'Finding Housing', path: '/integration/housing' },
              { icon: <Briefcase className="w-5 h-5" />, title: 'Opening a Business', path: '/integration/business-setup' },
              { icon: <GraduationCap className="w-5 h-5" />, title: 'Education & Training', path: '/integration/education' },
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="bg-white rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-primary">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HealthcareSystemPage;
